import { Box, Grid, IconButton, styled } from '@mui/material';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import StyledTextField from '../../Common/StyledTextField';
import { useAppSelector } from '../../../redux/hooks';
import { SenderType, sendMessage } from '../../../redux/chat/chatSlice';
import { AssetType } from '../../../redux/activity/activitySlice';
import CaptureAudio from '../components/CaptureAudio';
import createPermissionCheckers from '../../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../../types/commonTypes';
import ErrorModal from '../../ErrorModal/ErrorModal';

export const SendButton = styled(Grid)({
  background: 'var(--Brand-Cadet-Blue, linear-gradient(138deg, #6599A5 24.11%, #3B667E 83.2%))',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
});

interface ISendMessageProps {
  setRowCount: (_rowCount: number) => void;
  clientId: string;
  isClientTab?: boolean;
}

function SendMessage({ setRowCount, clientId, isClientTab }: Readonly<ISendMessageProps>) {
  const maxSizeImage = +(process.env.MAX_SIZE_IMAGE ?? 5 * 1024 * 1024);
  const maxSizeAudio = +(process.env.MAX_SIZE_AUDIO ?? 10 * 1024 * 1024);
  const maxSizeVideo = +(process.env.MAX_SIZE_VIDEO ?? 10 * 1024 * 1024);
  const maxSizeDocument = +(process.env.MAX_SIZE_DOCUMENT ?? 10 * 1024 * 1024);
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const coachId = useAppSelector((state) => state.coach.coach?.id);
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [key, setKey] = useState(0); // Key for forcing re-render
  const [recording, setRecording] = useState(false); // State to track whether recording is active
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(event.target.value);
    const textarea = event.target;
    textarea.style.height = 'auto'; // Reset the height to auto
    textarea.style.height = `${textarea.scrollHeight}px`; // Set the height to the scrollHeight
    const rows = Math.ceil(
      textarea.scrollHeight / parseFloat(getComputedStyle(textarea).lineHeight)
    );
    setRowCount(rows);
  };

  const handleSend = () => {
    if (!coachId || !clientId) return;
    const newMessage = {
      clientId,
      coachId,
      message,
      senderType: SenderType.COACH,
      chatMessageType: AssetType.TEXT,
      assetDuration: '0',
    };
    dispatch(sendMessage(newMessage));
    setMessage('');
    setRowCount(1);
    setKey((prevKey) => prevKey + 1);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const showErrorModal = (errMsg: string) => {
    setErrorMessage(errMsg);
    setIsErrorModalOpen(true);
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
    setErrorMessage('');
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      let maxSize: number;
      let assetType: AssetType;
      if (file.type.startsWith('image/')) {
        assetType = AssetType.IMAGE;
        maxSize = maxSizeImage;
      } else if (file.type.startsWith('video/')) {
        assetType = AssetType.VIDEO;
        maxSize = maxSizeVideo;
      } else if (file.type.startsWith('audio/')) {
        assetType = AssetType.AUDIO;
        maxSize = maxSizeAudio;
      } else if (file.type.startsWith('application/')) {
        assetType = AssetType.DOCUMENT;
        maxSize = maxSizeDocument;
      } else {
        assetType = AssetType.DOCUMENT;
        maxSize = maxSizeDocument;
      }

      if (file.size > maxSize) {
        showErrorModal(
          t('error.fileUpload.fileSizeTooLarge', {
            maxSize: +(Math.floor((maxSize / 1024 / 1024) * 10) / 10),
          })
        );
        return;
      }

      const newMessage = {
        clientId,
        coachId,
        message: file.name,
        senderType: SenderType.COACH,
        assetDuration: '0',
        chatMessageType: assetType,
        assetFile: file,
      };
      dispatch(sendMessage(newMessage));
    }
  };

  const handleUploadFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Reset file input to allow selecting the same file again
      fileInputRef.current.accept = 'image/*,video/*'; // Accept images, videos, and PDFs
      fileInputRef.current.click(); // Trigger click event of hidden file input
    }
  };

  return (
    <>
      {checkPermissions(UserPermissionType.CHAT_MODULE_SEND_MESSAGE) ? (
        <Grid
          p={isClientTab ? 1 : 2}
          mb={isClientTab ? 1 : 0}
          borderTop="1px solid #FFDDC8"
          gap={2}
          display="flex"
          flexDirection="row"
          alignItems="center"
        >
          {recording ? (
            <CaptureAudio hide={setRecording} clientId={clientId} />
          ) : (
            <Grid
              maxHeight={130}
              sx={{ overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: '#7CAEBE #fff' }}
              width="100%"
            >
              <StyledTextField
                key={key}
                fullWidth
                bordercolor="#FFDDC8"
                placeholder={t('activity.createActivity.placeholder')}
                multiline
                rows={1}
                padding="0px 12px"
                onChange={handleInputChange}
                value={message}
                onKeyDown={handleKeyPress}
                InputProps={{
                  endAdornment: (
                    <Grid display="flex" flexDirection="row" gap="12px" height="32px">
                      {!message && (
                        <>
                          <Box height={32} onClick={handleUploadFile}>
                            <img
                              src="/assets/icons/paper-clip.svg"
                              alt="paper clip"
                              style={{ cursor: 'pointer' }}
                            />
                          </Box>
                          <Box height={32} onClick={() => setRecording(true)}>
                            <img
                              src="/assets/icons/voice.svg"
                              alt="start recording"
                              style={{ cursor: 'pointer' }}
                              height={32}
                              width={32}
                            />
                          </Box>
                        </>
                      )}
                    </Grid>
                  ),
                }}
              />
            </Grid>
          )}
          {message && (
            <SendButton>
              <IconButton onClick={handleSend}>
                {' '}
                <img src="/assets/icons/plain.svg" alt="plain" />
              </IconButton>
            </SendButton>
          )}
        </Grid>
      ) : null}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <ErrorModal isOpen={isErrorModalOpen} message={errorMessage} onClose={closeErrorModal} />
    </>
  );
}

export default SendMessage;
